import { BusinessItem } from "@/core/models/businesses/businessList";
import { formatDate } from "@/lib/format";
import { slugify } from "@/lib/slugify";
import { getFullUrl } from "@/lib/utils";
import { faMapMarked } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { ENV } from "@/core/config/env";
import { useBusinessCompare } from "@/contexts/BusinessCompareContext";

interface Props {
    business: BusinessItem;
}

export const BusinessItemCard = ({ business }: Props) => {
    const businessUrl = getFullUrl(`automotive-provider/${business.companyId}/${slugify(business?.companyName || '')}`);
    const [categoriesExpanded, setCategoriesExpanded] = useState(false);
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const { compareBusinesses, addToCompare, removeFromCompare, isInCompare } = useBusinessCompare();
    const categories = (business.compCategory || '')
        .split(/[|,;]/)
        .map((category) => category.trim())
        .filter(Boolean);
    const visibleCategories = categoriesExpanded ? categories : categories.slice(0, 5);
    const hasMoreCategories = categories.length > 5;
    const rawDescription = (business.compDescription || '').replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
    const descriptionWords = rawDescription ? rawDescription.split(' ') : [];
    const truncatedDescription = descriptionWords.slice(0, 100).join(' ');
    const hasMoreDescription = descriptionWords.length > 100;
    const descriptionText = descriptionExpanded || !hasMoreDescription
        ? rawDescription
        : `${truncatedDescription}...`;
    const isCompared = isInCompare(business.companyId);
    const compareDisabled = !isCompared && compareBusinesses.length >= 2;

    return (
        <div className="basis-12/12 min-[550px]:basis-6/12 xl:basis-4/12 px-[12px]">
            <div className="relative px-3 py-3 product-box bg-white w-full shadow h-full flex flex-col">
                <div className="w-[100px] text-center uppercase absolute max-[451px]:-right-5 -right-4 top-10">
                    <div className="bg-primary text-white py-1 px-2 z-9 relative date-box after:w-0 after:h-0 after:border-tl-[40px] after:border-bl-[40px] after:border-[#861622] after:rotate-90 after:top-[51px] after:right-0">
                        <span className="text-[12px] font-semibold">
                            {formatDate(business.joinDate || "")}
                        </span>
                    </div>
                </div>

                <div className="relative overflow-hidden mb-4">
                    <Link href={businessUrl}>
                        <div className="aspect-2/1">
                            <Image
                                src={business.logoPath ? `${ENV.IMAGE_URL}${business.logoPath}` : '/images/no-image.webp'}
                                fill
                                loading="lazy"
                                className="w-full h-full object-contain"
                                alt={business.companyName}
                            />
                        </div>
                    </Link>
                </div>

                <Link href={businessUrl} className="uppercase text-primary font-semibold mb-1">
                    {business.companyName}
                </Link>

                <label className="mb-2 flex items-center gap-2 text-xs font-semibold text-gray-600">
                    <input
                        type="checkbox"
                        className="h-4 w-4 accent-[var(--primary-color)]"
                        checked={isCompared}
                        disabled={compareDisabled}
                        onChange={(event) => {
                            if (event.target.checked) {
                                addToCompare(business);
                            } else {
                                removeFromCompare(business.companyId);
                            }
                        }}
                    />
                    Compare
                </label>

                <ul className="list-disc ml-5 mb-2 min-h-[60px] flex flex-col justify-start gap-1">
                    {business.compProvinceName && (
                        <li className="text-sm font-light text-gray-500">{business.compProvinceName}</li>
                    )}
                    {business.compCityName && (
                        <li className="text-sm font-light text-gray-500">{business.compCityName}</li>
                    )}
                    {business.compSuburb && (
                        <li className="text-sm font-light text-gray-500">{business.compSuburb}</li>
                    )}
                </ul>

                <p className="text-sm text-black font-medium mb-2 flex items-start gap-1.5">
                    {business.compStreetAddress ? (
                        <>
                            <FontAwesomeIcon icon={faMapMarked} className="w-4 h-4 text-primary mt-0.5" />
                            <span
                                className="leading-snug break-words"
                                style={{ wordBreak: "break-word" }}
                                title={business.compStreetAddress}
                            >
                                {business.compStreetAddress}
                            </span>
                        </>
                    ) : (
                        <span className="h-6" />
                    )}
                </p>

                {rawDescription && (
                    <div className="mb-3 text-sm text-gray-600">
                        <p>{descriptionText}</p>
                        {hasMoreDescription && (
                            <button
                                type="button"
                                className="mt-2 text-xs font-semibold text-primary hover:underline"
                                onClick={() => setDescriptionExpanded((prev) => !prev)}
                            >
                                {descriptionExpanded ? "Show less" : "Show more"}
                            </button>
                        )}
                    </div>
                )}

                {categories.length > 0 && (
                    <div className="mb-3">
                        <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Categories</p>
                        <ul className="flex flex-wrap gap-2">
                            {visibleCategories.map((category) => (
                                <li key={category}>
                                    <Link
                                        href={`/spare-shops-near-me/?category=${encodeURIComponent(category)}`}
                                        className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 transition hover:bg-gray-200"
                                    >
                                        {category}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        {hasMoreCategories && (
                            <button
                                type="button"
                                className="mt-2 text-xs font-semibold text-primary hover:underline"
                                onClick={() => setCategoriesExpanded((prev) => !prev)}
                            >
                                {categoriesExpanded ? "Show fewer" : `View ${categories.length - 5} more`}
                            </button>
                        )}
                    </div>
                )}



                <div className="flex justify-between mt-auto">
                    <Link
                        href={businessUrl}
                        className="btn bg-[var(--primary-color)] hover:bg-white border border-[var(--primary-color)] text-sm flex items-center gap-1 text-white hover:text-[var(--primary-color)]"
                    >
                         Read More about {business.companyName}
                    </Link>
                </div>
            </div>
        </div>
    );
};